import React, { useState, useRef, useEffect, ReactNode } from 'react';
import {
  ChevronDown,
  Grip,
  ArrowUp,
  ArrowDown,
  Plus,
  Trash,
} from 'lucide-react';
import Tooltip from '../ui/TooltipSimple';
import { TOOLTIP_GAP } from '../ui/tooltipConsts';
import { WIDGET_REGISTRY } from '../../config/widgetRegistry';

// --- Types ---

interface SectionSeparatorProps {
  id: string | number;
  title: string;
  children?: ReactNode; // Made optional
  isOpen: boolean;
  isDragging?: boolean;
  /** Number of columns on md+ screens (2..5). If not provided defaults to 3 */
  cols?: number;
  onToggle: (id: string | number) => void;
  onRename: (id: string | number, newTitle: string) => void;
  onDelete: (id: string | number) => void;
  onSectionDrop?: (e: React.DragEvent<HTMLDivElement>, sectionId: string | number, insertIndex?: number) => void;
  onSectionDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onMoveLeft?: (id: string | number) => void;
  onMoveRight?: (id: string | number) => void;
  onMoveUp?: (id: string | number) => void;
  onMoveDown?: (id: string | number) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  onAddWidget?: (sectionId: string | number, widgetId: string) => void;
  /** Open the central WidgetsLauncher for this section (optional) */
  onOpenWidgetLauncher?: (sectionId: string | number) => void;
  theme?: 'light' | 'dark';
}

interface AddSectionButtonProps {
  onClick: () => void;
}

// --- Components ---

/**
 * SECTION SEPARATOR COMPONENT
 * A "Command Pill" style divider that allows grouping, collapsing, and managing dashboard sections.
 */
export const SectionSeparator: React.FC<SectionSeparatorProps> = ({ 
  id, 
  title, 
  children, 
  isOpen, 
  isDragging = false,
  onToggle, 
  onRename, 
  onDelete,
  onSectionDrop,
  onSectionDragOver,
  cols,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  dragHandleProps,
  onAddWidget,
  onOpenWidgetLauncher,
  theme = 'dark'
}) => {
  // isDragging is optionally provided by the parent to indicate an active drag session
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [tempTitle, setTempTitle] = useState<string>(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onRename(id, tempTitle);
      setIsEditing(false);
    }
    if (e.key === 'Escape') {
      setTempTitle(title);
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    onRename(id, tempTitle);
    setIsEditing(false);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Remove the ${title} section? Its widgets will be moved to the first remaining section.`)) {
      onDelete(id);
    }
  };

  // Track placeholder highlight while dragging
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!isAddOpen) return;
      if (addRef.current && !addRef.current.contains(e.target as Node)) {
        setIsAddOpen(false);
      }
    };
    window.addEventListener('pointerdown', handler);
    return () => window.removeEventListener('pointerdown', handler);
  }, [isAddOpen]);

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (typeof onOpenWidgetLauncher === 'function') {
        if (console.debug) console.debug(`[SectionSeparator] onOpenWidgetLauncher present for section ${id}`);
        onOpenWidgetLauncher(id);
        try { window.dispatchEvent(new CustomEvent('open-widgets-launcher', { detail: id })); } catch {}
        return;
      }
    } catch {
      // swallow any logging errors
    }
    if (console.debug) console.debug(`[SectionSeparator] no onOpenWidgetLauncher for section ${id}, toggling inline menu`);
    try { window.dispatchEvent(new CustomEvent('open-widgets-launcher', { detail: id })); } catch {}
    setIsAddOpen(o => !o);
  };

  const isDarkTheme = theme === 'dark';
  // Theme-separated configuration
  const lightTheme = {
    lineClassOpen: 'via-blue-400/70',
    lineClassClosed: 'via-blue-400',
    pillClassOpen: 'bg-[#0070b7] border-[#75d6ff] shadow-[0_12px_28px_rgba(15,23,42,0.08)] text-white',
    pillClassClosed: 'bg-zinc-200 border border-blue-400 text-blue-400 hover:border-blue-500',
    pillTitleOpen: 'text-white',
    pillTitleClosed: 'text-zinc-600',
    dividerOpen: 'bg-white/90',
    dividerClosed: 'bg-zinc-600/80',
    toggleOpenClass: 'text-white hover:text-blue-200',
    toggleClosedClass: 'text-slate-500 hover:text-slate-700',
    controlButtonBase: 'bg-white border border-blue-500 text-blue-500 hover:bg-blue-200 hover:text-blue-600',
    controlButtonHover: 'hover:text-blue-500 hover:border-blue-500 hover:bg-white hover:ring-2 hover:ring-blue-500',
    renameStrokeClass: 'ring-1 ring-blue-600/20',
    addMenuContainer: 'bg-white border-slate-200 text-slate-900',
    addMenuDivider: 'border-slate-200',
    addMenuItemHover: 'hover:bg-slate-100',
    addMenuBadge: 'text-slate-400',
    accentTextHover: 'hover:text-blue-600',
    placeholderInactiveClass: 'border border-dashed border-slate-200 text-slate-400 bg-white',
    placeholderActiveClass: 'border border-blue-600/70 bg-blue-50 text-blue-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]',
    emptyStateBoxClass: 'border-dashed border-slate-200 bg-slate-50 text-slate-500',
    addIconButtonClass: 'border-slate-300 text-slate-500 hover:text-blue-600 hover:border-blue-600 hover:bg-white',
    dropPlaceholderInactive: 'border border-dashed border-slate-200 bg-white text-slate-400',
    dropPlaceholderActive: 'border border-blue-600/70 bg-blue-50 text-blue-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]',
  };

  const darkTheme = {
    lineClassOpen: 'via-blue-500/70',
    lineClassClosed: 'via-blue-400',
    pillClassOpen: 'bg-[#161d2c] border-blue-500 shadow-[0_0_15px_rgba(37,99,235,0.15)]',
    pillClassClosed: 'bg-zinc-900 border-zinc-800 hover:border-blue-500/50',
    pillTitleOpen: 'text-blue-100',
    pillTitleClosed: 'text-zinc-500',
    dividerOpen: 'bg-blue-500',
    dividerClosed: 'bg-zinc-300/80',
    toggleOpenClass: 'text-blue-400 hover:text-blue-300',
    toggleClosedClass: 'text-zinc-600 hover:text-zinc-300',
    controlButtonBase: 'bg-zinc-950 border border-blue-500 text-white',
    controlButtonHover: 'hover:text-blue-600 hover:border-blue-600/80 hover:bg-white/100 hover:ring-2 hover:ring-blue-600',
    renameStrokeClass: '',
    addMenuContainer: 'bg-zinc-900 border-zinc-800 text-zinc-100',
    addMenuDivider: 'border-zinc-800',
    addMenuItemHover: 'hover:bg-zinc-800',
    addMenuBadge: 'text-zinc-500',
    accentTextHover: 'hover:text-blue-400',
    placeholderInactiveClass: 'border border-dashed border-zinc-800/80 text-zinc-600 bg-zinc-950/40',
    placeholderActiveClass: 'border border-blue-500/80 bg-blue-500/15 text-blue-100 shadow-[0_0_0_1px_rgba(37,99,235,0.55)]',
    emptyStateBoxClass: 'border-dashed border-zinc-800/50 bg-zinc-950/40 text-zinc-600',
    addIconButtonClass: 'border-2 border-blue-300 text-blue-300 hover:border-blue-700 hover:text-blue-900 hover:border-blue-500/100 hover:bg-white/100',
    dropPlaceholderInactive: 'border border-dashed border-zinc-800/80 bg-zinc-950/40 text-zinc-500',
    dropPlaceholderActive: 'border border-blue-500/70 bg-blue-500/10 text-blue-100 shadow-[0_0_0_1px_rgba(37,99,235,0.45)]',
  };

  const tv = isDarkTheme ? darkTheme : lightTheme;
  const {
    lineClassOpen,
    lineClassClosed,
    pillClassOpen,
    pillClassClosed,
    pillTitleOpen,
    pillTitleClosed,
    toggleOpenClass,
    toggleClosedClass,
    controlButtonBase,
    controlButtonHover,
    renameStrokeClass,
    addMenuContainer,
    addMenuDivider,
    addMenuItemHover,
    addMenuBadge,
    accentTextHover,
    placeholderInactiveClass,
    placeholderActiveClass,
    emptyStateBoxClass,
    addIconButtonClass,
    dropPlaceholderInactive,
    dropPlaceholderActive,
  } = tv;

  const buttonStrokeClass = renameStrokeClass;
  const addButtonClass = isDarkTheme
    ? `${controlButtonBase} ${controlButtonHover} ${accentTextHover} ${buttonStrokeClass}`
    : `${controlButtonBase} ${controlButtonHover} ${buttonStrokeClass}`;
  const iconStrokeWidth = 1;

  return (
    <div className={`${isOpen ? 'pt-8 pb-0 mb-0' : 'pt-4 pb-0 mb-0'} group/separator`}>
      {/* HEADER */}
      <div data-section-anchor={id} className={`relative flex items-center justify-center ${isOpen ? 'mb-9' : 'mb-0'}`}>
        {/* Divider Line */}
        <div 
          className={`
            absolute left-0 right-0 h-px bg-gradient-to-r from-transparent to-transparent transition-colors duration-500
            ${isOpen ? lineClassOpen : lineClassClosed}
          `}
        ></div>
        {/* Interactive Pill */}
        <div className="relative flex items-center gap-3">
            
            {/* Left-side controls: Delete + Down arrow (on the line) */}
            <div className="absolute right-full mr-3 flex items-center gap-2 z-20">
              <Tooltip text={`Remove ${title} section`} gap={TOOLTIP_GAP + 5}>
                <button 
                  type="button"
                  onClick={handleDeleteClick}
                  aria-label={`Remove ${title} section`}
                  className={`
                    flex h-10 w-10 items-center justify-center rounded-full border shadow-sm transition-[opacity,transform,background-color,color,border-color] duration-200
                    ${controlButtonBase} ${controlButtonHover} ${buttonStrokeClass} opacity-0 group-hover/separator:translate-x-0 group-hover/separator:opacity-100 group-focus-within/separator:translate-x-0 group-focus-within/separator:opacity-100 focus:opacity-100 [@media(hover:none)]:translate-x-0 [@media(hover:none)]:opacity-100 translate-x-2 hover:!bg-red-100 hover:!text-red-500 hover:border-red-500 hover:ring-2 hover:!ring-red-500
                  `}
                >
                  <Trash size={14} strokeWidth={2} />
                </button>
              </Tooltip>

              {canMoveDown && (
                <Tooltip text="Move section down" gap={TOOLTIP_GAP + 5}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); if (typeof (onMoveDown) === 'function') onMoveDown(id); }}
                    aria-label={`Move ${title} section down`}
                    className={`flex h-10 w-10 translate-x-2 items-center justify-center rounded-full opacity-0 shadow-sm transition-[opacity,transform,background-color,color,border-color] group-hover/separator:translate-x-0 group-hover/separator:opacity-100 group-focus-within/separator:translate-x-0 group-focus-within/separator:opacity-100 focus:opacity-100 [@media(hover:none)]:translate-x-0 [@media(hover:none)]:opacity-100 ${controlButtonBase} ${controlButtonHover} ${buttonStrokeClass}`}
                  >
                    <ArrowDown size={14} strokeWidth={2} />
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Right-side controls: Up arrow + Edit (on the line) */}
            <div className="absolute left-full ml-3 z-20 flex items-center gap-2">
              {canMoveUp && (
                <Tooltip text="Move section up" gap={TOOLTIP_GAP + 5}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); if (typeof (onMoveUp) === 'function') onMoveUp(id); }}
                    aria-label={`Move ${title} section up`}
                    className={`
                        flex h-10 w-10 -translate-x-2 items-center justify-center rounded-full
                        opacity-0 transition-[opacity,transform,background-color,color,border-color] group-hover/separator:translate-x-0 group-hover/separator:opacity-100 group-focus-within/separator:translate-x-0 group-focus-within/separator:opacity-100 focus:opacity-100 [@media(hover:none)]:translate-x-0 [@media(hover:none)]:opacity-100
                        ${controlButtonBase} ${controlButtonHover} ${buttonStrokeClass}
                      `}
                  >
                    <ArrowUp size={14} strokeWidth={2} />
                  </button>
                </Tooltip>
              )}
              <div className="relative">
                <Tooltip text="Add widget to this section" gap={TOOLTIP_GAP + 5}>
                  <button
                    type="button"
                    onClick={handleAddClick}
                    aria-label={`Add widget to ${title} section`}
                    className={`flex h-10 w-10 -translate-x-2 items-center justify-center rounded-full opacity-0 transition-[opacity,transform,background-color,color,border-color] group-hover/separator:translate-x-0 group-hover/separator:opacity-100 group-focus-within/separator:translate-x-0 group-focus-within/separator:opacity-100 focus:opacity-100 [@media(hover:none)]:translate-x-0 [@media(hover:none)]:opacity-100 ${addButtonClass}`}
                  >
                    <Plus size={16} strokeWidth={2} className={`${isDarkTheme ? 'text-blue-400' : 'text-blue-600'}`} />
                  </button>
                </Tooltip>
                {isAddOpen && (
                  <div ref={addRef} className={`absolute z-30 mt-2 right-0 w-64 rounded-xl shadow-lg p-2 ${addMenuContainer}`}>
                    <div className={`text-xs font-semibold px-2 py-1 border-b ${addMenuDivider}`}>Add Widget</div>
                    <div className="max-h-64 overflow-auto">
                      {Object.entries(WIDGET_REGISTRY).map(([wid, def]) => (
                        <button
                          key={wid}
                          onClick={(ev) => { ev.stopPropagation(); setIsAddOpen(false); if (typeof onAddWidget === 'function') onAddWidget(id, wid); }}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${addMenuItemHover}`}
                        >
                          <span className="flex-1">{def.title}</span>
                          <span className={`text-xs ${addMenuBadge}`}>ADD</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Pill UI */}
            <div 
              className={`
                relative flex items-center gap-3 px-5 py-1.5 rounded-full border transition-all shadow-sm z-10
                ${isOpen ? pillClassOpen : pillClassClosed}
              `}
            >
              <div className="flex items-center w-8 justify-center" {...dragHandleProps}>
                <Grip size={16} strokeWidth={iconStrokeWidth} className={`${isDarkTheme ? 'text-blue-300' : 'text-blue-600'} cursor-grab active:cursor-grabbing`} />
              </div>

              <div className="flex-1 flex items-center justify-center">
                {isEditing ? (
                  <input
                    ref={inputRef}
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    className="bg-transparent border-none outline-none text-[12px] tracking-widest font-semibold text-white uppercase w-full max-w-[240px] text-center [word-spacing:0.35em]"
                  />
                ) : (
                  <Tooltip text="Double-click to edit" gap={TOOLTIP_GAP + 5}>
                    <div
                      className="flex items-center justify-center cursor-text w-full"
                      onDoubleClick={() => setIsEditing(true)}
                    >
                      <span className={`text-[12px] tracking-widest font-semibold uppercase select-none [word-spacing:0.35em] ${isOpen ? pillTitleOpen : pillTitleClosed}`}>
                        {title}
                      </span>
                    </div>
                  </Tooltip>
                )}
              </div>

              <div className="flex items-center w-8 justify-center">
                <button
                  type="button"
                  onClick={() => onToggle(id)}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title} section`}
                  aria-expanded={isOpen}
                  className={`${isOpen ? toggleOpenClass : toggleClosedClass} inline-flex min-h-10 min-w-10 items-center justify-center rounded-full transition-colors`}>
                  <ChevronDown
                    size={16}
                    strokeWidth={2}
                    className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
              </div>

              {/* Section is draggable via the pill / grip handle for reordering */}
            </div>
        </div>
      </div>

        {/* CONTENT AREA */}
        {/* wrapper for children and placeholders */}
        <div
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (onSectionDragOver) onSectionDragOver(e); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (onSectionDrop) onSectionDrop(e, id); }}
          data-section-id={id}
          className={(() => {
            const childCount = React.Children.count(children);
            const c = (cols && [2,3,4,5].includes(cols)) ? cols : 3;
            const baseOpenClass = isOpen ? 'opacity-100 max-h-none overflow-visible' : 'max-h-0 opacity-0 overflow-hidden';
            return childCount === 1
              ? `w-full transition-all duration-500 ease-in-out ${baseOpenClass}`
              : `grid grid-cols-1 ${c === 2 ? 'md:grid-cols-2' : c === 3 ? 'md:grid-cols-3' : c === 4 ? 'md:grid-cols-4' : 'md:grid-cols-5'} gap-6 transition-all duration-500 ease-in-out ${baseOpenClass}`;
          })()}
        >
         {isDragging && (
           <div
             className={`col-span-full h-8 rounded-xl flex items-center justify-center text-[10px] font-semibold uppercase tracking-[0.32em] ${hoverIndex === -1 ? dropPlaceholderActive : dropPlaceholderInactive}`}
             onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(-1); if (onSectionDragOver) onSectionDragOver(e); }}
             onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(-1); if (onSectionDragOver) onSectionDragOver(e); }}
             onDragLeave={() => setHoverIndex(null)}
             onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(null); if (onSectionDrop) onSectionDrop(e, id, 0); }}
           >
             Drop Here
           </div>
         )}

         {children}

        {(() => {
          const childCount = React.Children.count(children);
          const c = (cols && [2,3,4,5].includes(cols)) ? cols : 3;
          if (!isDragging && childCount > 0) return null;

          if (childCount === 0 && !isDragging) {
            return (
              <div className={`col-span-full h-40 rounded-2xl flex flex-col items-center justify-center text-xs uppercase tracking-widest relative ${emptyStateBoxClass}`}>
                <div className="mb-3 relative">
                  <button
                    type="button"
                    onClick={handleAddClick}
                    className={`inline-flex items-center justify-center h-9 w-9 rounded-full bg-transparent ${addIconButtonClass}`}
                    aria-label="Add widget"
                  >
                    <Plus size={18} strokeWidth={2} className={`${isDarkTheme ? 'text-blue-400' : 'text-blue-600'}`} />
                  </button>
                  {isAddOpen && (
                    <div ref={addRef} className={`absolute z-30 mt-2 -left-32 w-64 rounded-xl shadow-lg p-2 ${addMenuContainer}`}>
                      <div className={`text-xs font-semibold px-2 py-1 border-b ${addMenuDivider}`}>Add Widget</div>
                      <div className="max-h-64 overflow-auto">
                        {Object.entries(WIDGET_REGISTRY).map(([wid, def]) => (
                          <button
                            key={wid}
                            onClick={(ev) => { ev.stopPropagation(); setIsAddOpen(false); if (typeof onAddWidget === 'function') onAddWidget(id, wid); }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${addMenuItemHover}`}
                          >
                            <span className="flex-1">{def.title}</span>
                            <span className={`text-xs ${addMenuBadge}`}>ADD</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-[10px] font-semibold">ADD WIDGET</div>
              </div>
            );
          }

          const rows = Math.max(1, Math.ceil(childCount / c) + (isDragging ? 1 : 0));
          const total = rows * c;
          const placeholders: React.ReactNode[] = [];
          for (let i = childCount; i < total; i++) {
            const active = hoverIndex === i;
            placeholders.push(
              <div
                key={`empty-${i}`}
                className={`col-span-1 h-[220px] rounded-2xl flex items-center justify-center text-[10px] font-semibold uppercase tracking-[0.32em] transition-colors duration-150 ${active ? placeholderActiveClass : placeholderInactiveClass}`}
                onDragEnter={(e) => { e.preventDefault(); setHoverIndex(i); if (onSectionDragOver) onSectionDragOver(e); }}
                onDragOver={(e) => { e.preventDefault(); setHoverIndex(i); if (onSectionDragOver) onSectionDragOver(e); }}
                onDragLeave={() => setHoverIndex(null)}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(null); if (onSectionDrop) onSectionDrop(e, id, i); }}
              >
                Drop Here
              </div>
            );
          }

          if (childCount === 0) {
            const active = hoverIndex !== null;
            return (
              <div
                className={`col-span-full h-[220px] rounded-2xl flex items-center justify-center text-[10px] font-semibold uppercase tracking-[0.32em] transition-colors duration-150 ${active ? placeholderActiveClass : placeholderInactiveClass}`}
                onDragEnter={(e) => { e.preventDefault(); setHoverIndex(0); if (onSectionDragOver) onSectionDragOver(e); }}
                onDragOver={(e) => { e.preventDefault(); setHoverIndex(0); if (onSectionDragOver) onSectionDragOver(e); }}
                onDragLeave={() => setHoverIndex(null)}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(null); if (onSectionDrop) onSectionDrop(e, id, 0); }}
              >
                ADD WIDGET
              </div>
            );
          }

          return placeholders;
        })()}

        {isDragging && (() => {
          const childCount = React.Children.count(children);
          if (childCount === 0) return null;
          return (
            <div
              className={`col-span-full h-8 rounded-xl border ${hoverIndex === childCount ? 'border-blue-500/70 bg-blue-500/10 shadow-[0_0_0_1px_rgba(37,99,235,0.45)]' : 'border-dashed border-zinc-800/80 bg-zinc-950/40'} flex items-center justify-center text-[10px] font-semibold uppercase tracking-[0.32em] text-zinc-500`}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(childCount); if (onSectionDragOver) onSectionDragOver(e); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(childCount); if (onSectionDragOver) onSectionDragOver(e); }}
              onDragLeave={() => setHoverIndex(null)}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIndex(null); if (onSectionDrop) onSectionDrop(e, id, childCount); }}
            >
              Drop Here
            </div>
          );
        })()}
        </div>
    </div>
  );
};

/**
 * ADD NEW SECTION BUTTON
 */
export const AddSectionButton: React.FC<AddSectionButtonProps> = ({ onClick }) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div className="relative flex items-center justify-center py-10">
      <button
        type="button"
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="relative z-10 inline-flex items-center gap-3 rounded-full border border-dashed border-zinc-300/70 bg-white px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-600 transition-colors duration-200 hover:border-blue-500 hover:text-blue-600 hover:ring-2 hover:ring-blue-500 ring-offset-0 group peer dark:border-zinc-700/70 dark:bg-[#050505] dark:text-zinc-400 dark:hover:text-blue-400"
        aria-label="Add new section"
      >
        
        <span className="flex h-7 w-7 items-center justify-center rounded-full border border-transparent bg-blue-600 text-white transition-colors duration-200 group-hover:bg-blue-700 dark:bg-blue-400 dark:text-zinc-900 dark:group-hover:bg-blue-500">
          <Plus size={16} strokeWidth={2} />
        </span>
        <span className="text-zinc-600 transition-colors duration-200 group-hover:text-blue-600 dark:text-zinc-400 dark:group-hover:text-blue-400">Add New Section</span>
      </button>

      <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-zinc-200/40 to-transparent transition-colors duration-200 peer-hover:via-blue-500/60 peer-hover:shadow-[0_0_8px_rgba(59,130,246,0.45)] peer-hover:via-zinc-300/50 dark:via-zinc-800/40"></div>
    </div>
  );
};
